import cn from 'classnames';
import { differenceInDays } from 'date-fns';
import { noop } from 'lodash';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';

import { ReactElement, useEffect, useMemo, useState } from 'react';
import {
  FacebookShareButton,
  LinkedinShareButton,
  TwitterShareButton,
} from 'react-share';

import { Icon, IconButton } from 'components';
import ActionMenu from 'components/ActionMenu';
import { addCohostModalInstance } from 'components/AddCohostModal';
import ButtonV3 from 'components/ComponentV2/ButtonV3';
import Confirmation from 'components/Confirmation';
import Modal from 'components/Modal';
import { Col, Row } from 'components/layout';

import { useCohost } from 'hooks/useCohost';
import { useCommon } from 'hooks/useCommon';
import { useEvent } from 'hooks/useEvent';
import { useNotification } from 'hooks/useNotification';
import { useCampaigns } from 'lib/contexts/campaigns-context';

import { CohostAttributes } from 'lib/models/cohost';
import { TouchpointState } from 'lib/models/opportunity';
import { TouchpointType } from 'lib/models/touchpoint';
import { PAGE_ROUTES } from 'lib/page-routes';
import { useAuth } from 'lib/providers/AuthProvider';
import { parseArrayResponse, parseResponse } from 'lib/utils/parser';

import { useSocietiesSearch } from 'lib/contexts/societies-search-context';
import Feedback from '../Feedback';
import Guests from '../Guests';
import Hosts from '../Hosts';
import Overview from '../Overview';
import Sidebar from '../Sidebar';
import { useAttendeesFilter } from '../useAttendeesFilter';

import styles from './EventDashboardModal.module.scss';

export enum ConfirmationScreen {
  NO_SCREEN = 0,
  STOP_COHOST = 1,
  DELETE_EVENT = 3,
  UNPUBLISH = 4,
}

export const EventDashboardModal = (): ReactElement => {
  const router = useRouter();
  const [t] = useTranslation('event-dashboard');

  const { id, tab } = router.query;

  const { setSearch } = useSocietiesSearch();

  const [selectedTab, setSelectedTab] = useState<string>(
    (tab as string) || 'overview'
  );

  useEffect(() => {
    setSelectedTab((tab as string) || 'overview');
  }, [tab]);

  const notificationInstance = useNotification();
  const { isManager } = useAuth();

  const showEventState = {
    expired: t('event-dashboard-modal.event-state-label.ended'),
    draft: t('event-dashboard-modal.event-state-label.draft'),
    ready_for_approval: t('event-dashboard-modal.event-state-label.in-review'),
  };

  const { touchpoint, mutateTouchpoint } = useAttendeesFilter();
  const { onDelete } = useCampaigns();
  const {
    title,
    short_slug,
    start_date,
    description,
    slug,
    cohost_application,
    state,
    creatable_for,
    is_online,
    place,
  } = touchpoint || {};
  const isHost = cohost_application ? false : true;
  const [inviteId, setInviteId] = useState<string>('');
  const coHost = parseResponse<CohostAttributes>(cohost_application);
  const {
    removeCohost,
    receivedCohostInvites,
    onAcceptInvite,
    onDeclineInvite,
    addCohost,
  } = useCohost();

  const isSocietyEvent = useMemo(() => {
    return creatable_for?.data.type === 'student_society';
  }, [creatable_for]);

  const editLink = useMemo(() => {
    return `${(isSocietyEvent
      ? PAGE_ROUTES.EVENTS_EDIT
      : PAGE_ROUTES.CAMPAIGNS_EDIT
    ).replace('[id]', id as string)}?step=1&type=${TouchpointType.Event}`;
  }, [id, isSocietyEvent]);

  const { eventStateChangeHandler, shouldPublishEvent } = useEvent();

  const { onShare } = useCommon();
  const pendingInvites =
    receivedCohostInvites && parseArrayResponse(receivedCohostInvites);
  const { id: hostId } = creatable_for
    ? parseResponse<{
        id: string;
      }>(creatable_for)
    : { id: '' };

  useEffect(() => {
    if (pendingInvites && pendingInvites.length > 0 && touchpoint) {
      const invite = pendingInvites.filter(
        (invite) =>
          invite.touchpoint_id === touchpoint.id &&
          invite.status === TouchpointState.PENDING
      );
      if (invite.length) {
        setInviteId(invite[0].id);
      } else {
        setInviteId('');
      }
    } else {
      setInviteId('');
    }
  }, [pendingInvites, touchpoint]);

  const [isButtonLoading, setIsButtonLoading] = useState<boolean>(false);
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] =
    useState<boolean>(false);
  const [confirmationScreenSelected, setConfirmationScreenSelected] =
    useState<ConfirmationScreen>(ConfirmationScreen.NO_SCREEN);

  const deleteEventConfirmationScreenDesc = touchpoint ? (
    <div className={styles.modalText}>
      <p>
        {t('event-dashboard-modal.delete-event-confirmation.body1')}
        <span>{title}</span>?
        <br />
        <br />
        {t('event-dashboard-modal.delete-event-confirmation.body2')}
        <br />
        <br />
        <div className={styles.action}>
          <span
            onClick={async () => {
              await onUnpublishEventHandler();
            }}
          >
            {t('event-dashboard-modal.delete-event-confirmation.unpublish')}
          </span>{' '}
          {t('event-dashboard-modal.delete-event-confirmation.body3')}
        </div>
      </p>
    </div>
  ) : null;

  const unpublishEventConfirmationScreenDesc = touchpoint ? (
    <div className={styles.modalText}>
      <p>
        {t('event-dashboard-modal.unpublish-event-confirmation.message')}
        <span>{title}</span>?
      </p>
    </div>
  ) : null;

  const stopHostConfirmationScreenDesc = title ? (
    <div className={styles.removeCohostModalText}>
      <p>
        {t('event-dashboard-modal.stop-co-hosting-confirmation.message')}
        <span>{title}</span>?
      </p>
    </div>
  ) : null;

  const ConfirmationScreenProps = [
    {
      id: ConfirmationScreen.DELETE_EVENT,
      title: t('event-dashboard-modal.delete-event-confirmation.title'),
      desc: deleteEventConfirmationScreenDesc,
      buttonContent: t(
        'event-dashboard-modal.delete-event-confirmation.button'
      ),
      onCancel: () => {
        setIsConfirmationModalOpen(false);
        setConfirmationScreenSelected(ConfirmationScreen.NO_SCREEN);
      },
      onConfirm: async () => {
        setIsButtonLoading(true);
        await onDelete(id as string);
        router.push(PAGE_ROUTES.CAMPAIGNS);
        setIsButtonLoading(false);
        setIsConfirmationModalOpen(false);
        setConfirmationScreenSelected(ConfirmationScreen.NO_SCREEN);
      },
      isPrimary: false,
    },
    {
      id: ConfirmationScreen.UNPUBLISH,
      title: t('event-dashboard-modal.unpublish-event-confirmation.title'),
      desc: unpublishEventConfirmationScreenDesc,
      buttonContent: t(
        'event-dashboard-modal.unpublish-event-confirmation.button'
      ),
      onCancel: () => {
        setIsConfirmationModalOpen(false);
        setConfirmationScreenSelected(ConfirmationScreen.NO_SCREEN);
      },
      onConfirm: async () => {
        setIsButtonLoading(true);
        await onUnpublishEventHandler();

        setIsButtonLoading(false);
      },
      isPrimary: false,
    },
    {
      id: ConfirmationScreen.STOP_COHOST,
      title: t('event-dashboard-modal.stop-co-hosting-confirmation.title'),
      desc: stopHostConfirmationScreenDesc,
      buttonContent: t(
        'event-dashboard-modal.stop-co-hosting-confirmation.button'
      ),
      onCancel: () => {
        setIsConfirmationModalOpen(false);
        setConfirmationScreenSelected(ConfirmationScreen.NO_SCREEN);
      },
      onConfirm: async () => {
        if (coHost && coHost.id) {
          setIsButtonLoading(true);
          await removeCohost(coHost.id);
          setIsButtonLoading(false);
          setIsConfirmationModalOpen(false);
          setConfirmationScreenSelected(ConfirmationScreen.NO_SCREEN);
          router.push(PAGE_ROUTES.CAMPAIGNS);
        }
      },
      isPrimary: false,
    },
  ];

  const getConfirmationModal = () => {
    const screen = ConfirmationScreenProps.find(
      (item) => item.id === confirmationScreenSelected
    );
    if (screen) {
      const { title, desc, buttonContent, onCancel, onConfirm, isPrimary } =
        screen;
      return (
        <Modal
          isOpen={isConfirmationModalOpen}
          onRequestClose={() => setIsConfirmationModalOpen(false)}
          isFullScreen={false}
        >
          <Confirmation
            title={title}
            desc={desc || ''}
            buttonContent={buttonContent}
            onCancel={onCancel}
            isLoading={isButtonLoading}
            onConfirm={onConfirm}
            isPrimary={isPrimary}
            secondaryActionOnRight={true}
          />
        </Modal>
      );
    }
  };

  const publicPageUrl = useMemo(
    () => `${window.location.origin}/events/${slug}`,
    [slug]
  );

  const onUnpublishEventHandler = async () => {
    try {
      const response = await eventStateChangeHandler({
        state: state as TouchpointState,
        id: id as string,
        unpublish: true,
      });
      if (response) {
        mutateTouchpoint();
        setIsConfirmationModalOpen(false);
        setConfirmationScreenSelected(ConfirmationScreen.NO_SCREEN);
        notificationInstance.success({
          title: t(
            'event-dashboard-modal.unpublish-event-confirmation.success-message-title'
          ),
          message: `${title} ${t(
            'event-dashboard-modal.unpublish-event-confirmation.success-message'
          )}`,
        });
      }
    } catch (error) {
      console.error(error);
      notificationInstance.handleExceptionError(error);
    }
  };

  const getShareMenu = () => {
    return [
      {
        id: '1',
        label: t('event-dashboard-modal.share-menu.copy'),
        onClick: (event: React.MouseEvent<HTMLButtonElement>) => {
          event.preventDefault();
          onShare(publicPageUrl, publicPageUrl);
        },
      },
      {
        id: '2',
        label: (
          <FacebookShareButton title={title} url={publicPageUrl}>
            {t('event-dashboard-modal.share-menu.share-on-facebook')}
          </FacebookShareButton>
        ),
        onClick: (event: React.MouseEvent<HTMLButtonElement>) => {
          event.preventDefault();
        },
      },
      {
        id: '3',
        label: (
          <TwitterShareButton title={title} url={publicPageUrl}>
            {t('event-dashboard-modal.share-menu.share-on-twitter')}
          </TwitterShareButton>
        ),
        onClick: (event: React.MouseEvent<HTMLButtonElement>) => {
          event.preventDefault();
        },
      },
      {
        id: '4',
        label: (
          <LinkedinShareButton
            title={title}
            source={process.env.NEXT_PUBLIC_SITE_URL}
            summary={description}
            url={publicPageUrl}
          >
            {t('event-dashboard-modal.share-menu.share-on-linkedin')}
          </LinkedinShareButton>
        ),
        onClick: (event: React.MouseEvent<HTMLButtonElement>) => {
          event.preventDefault();
        },
      },
    ];
  };

  const onAddCohostClick = async () => {
    if (hostId) {
      try {
        const cohostIds = await addCohostModalInstance({
          idsForCohost: {
            creatableForId: hostId,
            touchpointId: id as string,
          },
        });
        if (cohostIds.length > 0) {
          for (let i = 0; i < cohostIds.length; i++) {
            const response = await addCohost(id as string, cohostIds[i]);
            if (!response) {
              break;
            } else {
              mutateTouchpoint();
              notificationInstance.success({
                title:
                  cohostIds.length === 1
                    ? t('add-co-host-message.invitation-title')
                    : t('add-co-host-message.invitations-title'),
                message:
                  cohostIds.length === 1
                    ? t('add-co-host-message.invitation-message')
                    : t('add-co-host-message.invitations-message'),
              });
            }
          }
        }
        setSearch('');
      } catch (error) {
        noop;
      }
    }
  };

  const getCommonActionMenu = useMemo(() => {
    const menu = [
      {
        id: '1',
        label: t('event-dashboard-modal.share-menu.title'),
        onClick: (event: React.MouseEvent<HTMLButtonElement>) => {
          event.preventDefault();
        },
        options: getShareMenu(),
      },

      ...(isHost && isManager
        ? [
            {
              id: '2',
              label:
                state === TouchpointState.DRAFT
                  ? t('common.publish')
                  : t('common.unpublish'),
              onClick: async (event: React.MouseEvent<HTMLButtonElement>) => {
                event.preventDefault();
                if (state !== TouchpointState.DRAFT) {
                  setIsConfirmationModalOpen(true);
                  setConfirmationScreenSelected(ConfirmationScreen.UNPUBLISH);
                } else {
                  const isEventValid = await shouldPublishEvent(id as string);
                  if (!isEventValid) {
                    notificationInstance.warning({
                      title: t(
                        'event-dashboard-modal.publish-event-messages.error-message-title'
                      ),
                      message: t(
                        'event-dashboard-modal.publish-event-messages.error-message'
                      ),
                    });
                    router.push(editLink);
                  } else {
                    try {
                      const response = await eventStateChangeHandler({
                        state: state as TouchpointState,
                        id: id as string,
                      });
                      if (response) {
                        mutateTouchpoint();
                        notificationInstance.success({
                          title: t(
                            'event-dashboard-modal.publish-event-messages.success-message-title'
                          ),
                          message: `${title} ${t(
                            'event-dashboard-modal.publish-event-messages.success-message'
                          )}`,
                        });
                      }
                    } catch (error) {
                      console.error(error);
                      notificationInstance.handleExceptionError(error);
                    }
                  }
                }
              },
            },
          ]
        : []),
      ...(!isHost && !inviteId
        ? [
            {
              id: '2',
              label: t(
                'event-dashboard-modal.stop-co-hosting-confirmation.title'
              ),
              danger: true,
              onClick: async (event: React.MouseEvent<HTMLButtonElement>) => {
                event.preventDefault();
                event.stopPropagation();
                await removeCohost(coHost.id);
                router.push(PAGE_ROUTES.CAMPAIGNS);
              },
            },
          ]
        : []),
      ...(isHost
        ? [
            {
              id: '3',
              label: t('common.invite-co-host'),
              onClick: (event: React.MouseEvent<HTMLButtonElement>) => {
                event.preventDefault();
                onAddCohostClick();
              },
            },
            {
              id: '4',
              label: t('common.delete'),
              danger: true,
              onClick: async (event: React.MouseEvent<HTMLButtonElement>) => {
                event.preventDefault();
                event.stopPropagation();
                setIsConfirmationModalOpen(true);
                setConfirmationScreenSelected(ConfirmationScreen.DELETE_EVENT);
              },
            },
          ]
        : []),
      ...(inviteId
        ? [
            {
              id: '2',
              label: t('common.accept-co-hosting'),
              danger: false,
              onClick: async (event: React.MouseEvent<HTMLButtonElement>) => {
                event.preventDefault();
                event.stopPropagation();
                await onAcceptInvite(inviteId);
              },
            },
            {
              id: '3',
              label: t('common.decline-co-hosting'),
              danger: true,
              onClick: async (event: React.MouseEvent<HTMLButtonElement>) => {
                event.preventDefault();
                event.stopPropagation();
                await onDeclineInvite(inviteId);
                router.push(PAGE_ROUTES.CAMPAIGNS);
              },
            },
          ]
        : []),
    ];

    return menu;
  }, [inviteId, isHost, getShareMenu]);

  const showState = showEventState[state as keyof typeof showEventState];

  return (
    <Modal
      isOpen={true}
      onRequestClose={() => {
        router.push(PAGE_ROUTES.CAMPAIGNS);
      }}
      isFullScreen={true}
      className={styles.eventDashboardModal}
    >
      <Row className={styles.modalHeader} justify="space-between">
        <Row align="center" wrap={false} className={styles.modalHeaderName}>
          <Col>
            <IconButton
              size="small"
              iconName="close"
              className={styles.cancelIcon}
              onClick={() => {
                router.push(PAGE_ROUTES.CAMPAIGNS);
              }}
            />
          </Col>
          <Col className={styles.modalHeaderTitleWrapper}>
            <p className={styles.modalHeaderTitle}>{title}</p>
          </Col>
          <Col>
            <span
              className={cn(styles.stateBadge, {
                [styles.ended]: showState === 'Ended',
                [styles.draft]: showState === 'Draft',
                [styles.inReview]: showState === 'In review',
              })}
            >
              {showState}
            </span>
          </Col>
        </Row>
        <Row className={styles.buttonsRow}>
          {!is_online &&
          !place?.data &&
          differenceInDays(new Date(start_date), new Date()) < 3 ? (
            <>
              <ButtonV3
                className={styles.buttons}
                size="small"
                borderRadius={8}
                color={'secondary'}
                onClick={() => {
                  window.open(
                    `${window.location.origin}/${short_slug}`,
                    '_blank'
                  );
                }}
                startIcon="icon_launch"
              >
                {t('common.event-page')}
              </ButtonV3>
            </>
          ) : (
            <div className={styles.eventLinkWrapper}>
              <Icon iconName="icon_link" />
              <span>{`${window.location.origin}/${short_slug}`}</span>
              <div className={styles.iconButtonWrapper}>
                <div
                  className={styles.iconButton}
                  onClick={() => {
                    onShare(title, `${window.location.origin}/${short_slug}`);
                  }}
                >
                  <Icon iconName="icon_copy" size="xsmall" />
                </div>
                <div
                  className={styles.iconButton}
                  onClick={() => {
                    window.open(
                      `${window.location.origin}/${short_slug}`,
                      '_blank'
                    );
                  }}
                >
                  <Icon iconName="icon_launch" size="xsmall" />
                </div>
              </div>
            </div>
          )}
          {isHost && state !== 'expired' && (
            <ButtonV3
              className={styles.buttons}
              size="small"
              borderRadius={8}
              color={state === 'draft' ? 'primary' : 'secondary'}
              onClick={() => {
                router.push(editLink);
              }}
            >
              {state === 'draft' ? t('common.continue') : t('common.edit')}
            </ButtonV3>
          )}
          <ActionMenu
            options={getCommonActionMenu}
            triggerButtonClassName={styles.actionMenu}
          />
        </Row>
      </Row>
      <div className={styles.modalBody}>
        <div className={styles.modalContent}>
          <Sidebar selectedTab={selectedTab} setSelectedTab={setSelectedTab} />
          {selectedTab === 'overview' && <Overview />}
          {selectedTab === 'hosts' && <Hosts />}
          {selectedTab === 'feedback' && <Feedback />}
          {selectedTab === 'guests' && <Guests />}
        </div>
      </div>
      {isConfirmationModalOpen ? getConfirmationModal() : null}
    </Modal>
  );
};
